import { apiAction, apiFetch } from 'api/api'
import React, { useEffect } from 'react'
import { numberWithCommas } from 'utils/Utils'
import CustomTable from 'components/tables/CustomTable'
import CustomButton from 'components/button/CustomButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomInputElement from 'components/forms/CustomInputElement'
import { convertDataBasedOnYear } from 'helpers/TableComponentHelper'
import SectionError from 'components/error/SectionError'


import {
    Row,
    Col,
    Collapse,
} from 'reactstrap'

import {
    Box,
    Grid,
    ListItem,
    ListItemText,
    Slider,
    Tooltip,
    styled,
    Typography
} from '@mui/material'
import Loader from 'components/loader/Loader'

const T = {
    bg: '#F6F5F0',
    card: '#FFFFFF',
    line: '#E5E2D8',
    ink: '#232821',
    mut: '#6E766C',
    acc: '#2E9E7A',
};

const AnalysisContainer = (props) => {
    const { postUrl, url, title, cardTextStart, cardTextEnd, inputType, analysis, dcfKey } = props

    const [guide, setGuide] = React.useState([]);
    const [open, setOpen] = React.useState(true);
    const [value, setValue] = React.useState(50);
    const [result, setResult] = React.useState([]);
    const [data, setData] = React.useState(analysis);
    const [color, setColor] = React.useState('#BDBDBD');
    const [fetchError, setFetchError] = React.useState(null);
    const [retryTick, setRetryTick] = React.useState(0);

    useEffect(() => {
        const getApiResults = async () => {
            setFetchError(null);
            const { data: res, error } = await apiFetch({ url })
            if (error) {
                setFetchError(error);
                return;
            }
            if (res) {
                // Blocker 4: destructure instead of delete to avoid mutating the API response object
                const { guide: guideData, ...restRes } = res;
                setGuide(guideData)
                // Blocker 2: pure functional update — no in-place mutation of state
                setData(prev => {
                    const next = { ...prev };
                    Object.keys(restRes).forEach(item => {
                        if (next[item]) next[item] = { ...next[item], value: restRes[item] };
                    });
                    if (guideData) {
                        guideData.forEach(item => {
                            if (next[item.input]) {
                                next[item.input] = {
                                    ...next[item.input],
                                    value: (item.conservative - item.aggresive) / 1.6 + item.aggresive,
                                };
                            }
                        });
                    }
                    return next;
                });
            }
        }
        getApiResults()
        // eslint-disable-next-line
    }, [url, retryTick])

    const [outputValues, setOutputValues] = React.useState({});

    // Auto-calculate on input changes
    useEffect(() => {
        const handler = setTimeout(async () => {
            const notEnterdItems = Object.keys(data).filter((item) => {
                if (!data[item].readOnly && !data[item].ignore && (data[item].value === '' || data[item].value === null || data[item].value === undefined)) {
                    return true;
                }
                return false;
            })

            if (!notEnterdItems.length) {
                let body = {}
                Object.keys(data).map((item) => {
                    body[item] = data[item].value
                    return body
                })
                let res = await apiAction({
                    url: postUrl,
                    method: 'post',
                    data: body
                })
                if (dcfKey) {
                    setResult(res ? res : [])
                } else {
                    if (res) {
                        setOutputValues(res);
                    }
                }
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [data, postUrl, dcfKey]);

    const { rows, columnHeaders } = (result && result.fcf_pv_dict && dcfKey) 
        ? convertDataBasedOnYear(result.fcf_pv_dict) 
        : { rows: null, columnHeaders: null };

    const toggle = () => {
        setOpen(!open);
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
        if (value > 50) onSlideSetTryValue('aggresive')
        if (value < 50) onSlideSetTryValue('conservative')
        if (value === 50) {
            setColor('#FF9800')
            // Blocker 2: pure functional update
            setData(prev => {
                const next = { ...prev };
                Object.keys(guide).forEach(item => {
                    if (next[guide[item].input]) {
                        next[guide[item].input] = {
                            ...next[guide[item].input],
                            value: (guide[item].conservative - guide[item].aggresive) / 1.6 + guide[item].aggresive,
                        };
                    }
                });
                return next;
            });
        }
    }

    const onSlideSetTryValue = (v) => {
        // Blocker 2: compute new values without mutating state in place
        const computeValue = (item) => {
            const g = guide[item];
            if (!data[g.input]) return null;
            if (v === 'aggresive') {
                if (value === 60)  return (g.aggresive - g.conservative) / 1.5 + g.conservative;
                if (value === 70)  return (g.aggresive - g.conservative) / 1.4 + g.conservative;
                if (value === 80)  return (g.aggresive - g.conservative) / 1.3 + g.conservative;
                if (value === 90)  return (g.aggresive - g.conservative) / 1.2 + g.conservative;
                if (value === 100) return (g.aggresive - g.conservative) / 1   + g.conservative;
            } else {
                if (value === 40) return (g.conservative - g.aggresive) / 1.5 + g.aggresive;
                if (value === 30) return (g.conservative - g.aggresive) / 1.4 + g.aggresive;
                if (value === 20) return (g.conservative - g.aggresive) / 1.3 + g.aggresive;
                if (value === 10) return (g.conservative - g.aggresive) / 1.2 + g.aggresive;
                if (value === 0)  return (g.conservative - g.aggresive) / 1   + g.aggresive;
            }
            return null;
        };
        if (v === 'aggresive') setColor('#FF9800');
        else setColor('#71D875');
        setData(prev => {
            const next = { ...prev };
            Object.keys(guide).forEach(item => {
                const newVal = computeValue(item);
                if (newVal !== null && next[guide[item].input]) {
                    next[guide[item].input] = { ...next[guide[item].input], value: newVal };
                }
            });
            return next;
        });
    }

    return (
        <div style={{
            background: T.card,
            borderRadius: 16,
            border: `1px solid ${T.line}`,
            padding: '24px 20px',
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }}>
            {!guide.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 18, fontWeight: 700, color: T.ink }}>
                        {title}
                    </div>
                    {fetchError ? (
                        <SectionError message={fetchError.message} onRetry={() => setRetryTick(t => t + 1)} />
                    ) : (
                        <Loader />
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 8 }}>
                        {title}
                    </div>

                    <div style={{ fontSize: 11, color: T.mut, marginBottom: 16, lineHeight: 1.5 }}>
                        Input the values of <span style={{ fontWeight: 600, color: T.ink }}>{cardTextStart}</span> to calculate the Fair Value {cardTextEnd}
                    </div>

                    {/* Guide Toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div
                            style={{ fontSize: 14, fontWeight: 600, color: T.ink, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={toggle}
                        >
                            Guide
                            {!open && <InfoOutlinedIcon sx={{ fontSize: 16, color: T.mut }} />}
                        </div>
                        {open && <CommonHeader onClick={toggle} />}
                    </div>

                    <Collapse isOpen={open}>
                        {guide && (
                            <div style={{ background: T.bg, borderRadius: 12, padding: '16px', marginBottom: 20, border: `1px solid ${T.line}` }}>
                                {guide.map((item, index) => (
                                    <div key={index} style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
                                            {item.parameter_name}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <CommonToolTip color={'#4fce54'} title={'conservative'} value={item.conservative} />
                                            <CommonToolTip color={'#ff9800'} title={'aggresive'} value={item.aggresive} />
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: T.line, margin: '16px 0' }} />

                                <Box sx={{ px: 2, mb: 1 }}>
                                    <Slider
                                        step={10}
                                        sx={{ color: color, padding: '13px 0' }}
                                        value={value}
                                        defaultValue={50}
                                        onChange={handleChange}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                                    <TinyText sx={{ color: color === '#71D875' ? '#71D875' : T.mut, fontWeight: 700 }}>Conservative</TinyText>
                                    <TinyText sx={{ color: color === '#FF9800' ? '#FF9800' : T.mut, fontWeight: 700 }}>Aggresive</TinyText>
                                </Box>
                            </div>
                        )}
                    </Collapse>

                    {/* Form Grid */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Row>
                            {Object.keys(data).filter(item => !data[item].readOnly).map((item, index) => (
                                <Col xs={data[item].col} key={index} style={{ marginBottom: 4 }}>
                                    <CustomInputElement
                                        inputType={inputType}
                                        formGroupClass="mb-0"
                                        formGroupStyle={{}}
                                        labelMargin="0 0 4px 0"
                                        style={{
                                            ...data[item].style,
                                            fontSize: 14,
                                            fontWeight: 700,
                                            color: T.ink,
                                            background: '#F0EEE6',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '10px 12px',
                                            boxShadow: 'none',
                                            height: 40,
                                        }}
                                        requiredState={data[item].requiredState}
                                        label={<span style={{ color: T.mut, display: 'block' }}>{data[item].lable}</span>}
                                        placeHolder={data[item].placeholder}
                                        value={data[item].value}
                                        readOnly={data[item].readOnly}
                                        onChange={(e) => {
                                            // Blocker 2: pure functional update
                                            setData(prev => ({
                                                ...prev,
                                                [item]: { ...prev[item], requiredState: '', value: e.target.value },
                                            }));
                                        }}
                                    />
                                </Col>
                            ))}
                        </Row>

                        {Object.keys(data).filter(item => data[item].readOnly).length > 0 && (
                            <>
                                <hr style={{ borderColor: T.line, margin: '8px 0 12px 0' }} />
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px 16px',
                                }}>
                                    {Object.keys(data).filter(item => data[item].readOnly).map((item, index) => {
                                        const val = outputValues[item] !== undefined ? outputValues[item] : data[item].value;
                                        return (
                                            <div key={index} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '4px 0',
                                                gap: 8,
                                            }}>
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: T.mut,
                                                    lineHeight: 1.3,
                                                    flex: 1,
                                                }}>
                                                    {data[item].lable}
                                                </span>
                                                <span style={{
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    color: T.ink,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {val || '–'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Results */}
                    {rows && (
                        <div style={{ marginTop: 24, borderTop: `1px solid ${T.line}`, paddingTop: 24 }}>
                            <CustomTable
                                striped={false}
                                responsive={true}
                                hover={true}
                                data={rows}
                                columns={columnHeaders}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: T.mut }}>DCF:</span>
                                <span style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>₹ {numberWithCommas(result.dcf_per_share)}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const TinyText = styled(Typography)({
    fontSize: '0.75rem',
    letterSpacing: 0.2,
});

const CommonHeader = (props) => {
    const list = [
        { name: 'conservative', backgroundColor: '#6fd773' },
        { name: 'aggresive', backgroundColor: '#ff9800' }
    ]
    return (
        <div style={{ display: 'flex', gap: 12, cursor: 'pointer' }} onClick={props.onClick}>
            {list.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.backgroundColor }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.mut, textTransform: 'capitalize' }}>{item.name}</span>
                </div>
            ))}
        </div>
    )
}

const CommonToolTip = (props) => {
    const { color, title, value } = props
    return (
        <Tooltip title={title} arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        color: '#0d0d0d',
                        fontSize: '11px',
                        fontWeight: '600',
                        bgcolor: '#ebf0ff',
                        textTransform: 'capitalize',
                        '& .MuiTooltip-arrow': { color: '#ebf0ff' },
                    },
                },
            }} >
            <div style={{ padding: '2px 8px', borderRadius: 4, background: '#FFFFFF', border: `1px solid ${color}`, display: 'inline-flex' }}>
                <span style={{ color: color, fontSize: 12, fontWeight: 600 }}>{numberWithCommas(value)}</span>
            </div>
        </Tooltip>
    )
}

export default AnalysisContainer